export type RuntimeMode = 'qa' | 'production'

function trimmed(value: string | undefined): string {
  return value?.trim() ?? ''
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/u, '')
}

function browserHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase()
}

const PRODUCTION_PAGES_HOST = 'obelanse.pages.dev'
const PAGES_PREVIEW_SUFFIX = '.obelanse.pages.dev'
const RELEASE_PREVIEW_HOST = 'release-obelanse-v1.obelanse.pages.dev'
const SPRINT16_PREVIEW_HOST = 'sprint16-staging-preparation.obelanse.pages.dev'
const SPRINT17_PREVIEW_HOST = 'sprint17-communications-cont.obelanse.pages.dev'
const DELIVERY_PREVIEW_HOST = 'delivery-scope-freeze.obelanse.pages.dev'
const QA_SUPABASE_URL = 'https://nichqgydpyucciwetrbo.supabase.co'
const QA_SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_baDOaO7SccBL5IpgCQtkpg_Je5zyUq0'
const QA_WORKER_ORIGIN = 'https://obelanseqaworker.summer-firefly-d022.workers.dev'
const PREVIEW_API_BY_HOST = Object.freeze<Record<string, string>>({
  [RELEASE_PREVIEW_HOST]: 'https://release-obelanse-v1-obelanse-api-v1-staging.summer-firefly-d022.workers.dev',
  [SPRINT16_PREVIEW_HOST]: QA_WORKER_ORIGIN,
  [SPRINT17_PREVIEW_HOST]: QA_WORKER_ORIGIN,
  [DELIVERY_PREVIEW_HOST]: QA_WORKER_ORIGIN,
})
const PREVIEW_SUPABASE_BY_HOST = Object.freeze<Record<string, Readonly<{ url: string; publishableKey: string }>>>({
  [SPRINT16_PREVIEW_HOST]: Object.freeze({
    url: QA_SUPABASE_URL,
    publishableKey: QA_SUPABASE_PUBLISHABLE_KEY,
  }),
  [SPRINT17_PREVIEW_HOST]: Object.freeze({
    url: QA_SUPABASE_URL,
    publishableKey: QA_SUPABASE_PUBLISHABLE_KEY,
  }),
  [DELIVERY_PREVIEW_HOST]: Object.freeze({
    url: QA_SUPABASE_URL,
    publishableKey: QA_SUPABASE_PUBLISHABLE_KEY,
  }),
})

function isPagesPreview(hostname: string): boolean {
  return hostname.endsWith(PAGES_PREVIEW_SUFFIX) && hostname !== PRODUCTION_PAGES_HOST
}

function isApprovedPagesPreview(hostname: string): boolean {
  return Boolean(PREVIEW_API_BY_HOST[hostname])
}

function resolveApiBaseUrl(configuredApiBaseUrl: string): string {
  const hostname = browserHostname()
  const previewApi = PREVIEW_API_BY_HOST[hostname]
  if (previewApi) return previewApi
  if (isPagesPreview(hostname)) return ''
  return configuredApiBaseUrl
}

function resolveSupabaseUrl(configuredSupabaseUrl: string): string {
  const hostname = browserHostname()
  const previewSupabase = PREVIEW_SUPABASE_BY_HOST[hostname]
  if (previewSupabase) return previewSupabase.url
  if (isPagesPreview(hostname) && !isApprovedPagesPreview(hostname)) return ''
  return configuredSupabaseUrl
}

function resolveSupabasePublishableKey(configuredPublishableKey: string): string {
  const hostname = browserHostname()
  const previewSupabase = PREVIEW_SUPABASE_BY_HOST[hostname]
  if (previewSupabase) return previewSupabase.publishableKey
  if (isPagesPreview(hostname) && !isApprovedPagesPreview(hostname)) return ''
  return configuredPublishableKey
}

const hostname = browserHostname()
const approvedPagesPreview = isApprovedPagesPreview(hostname)
const requestedMode = trimmed(import.meta.env.VITE_APP_MODE)

// Approved Cloudflare Pages preview hosts must render the reviewed production/Figma
// application while continuing to use their explicitly mapped QA API/Supabase data.
// The deterministic QaApp remains available for local/test builds that explicitly
// run outside an approved preview host.
const mode: RuntimeMode = requestedMode === 'production' || approvedPagesPreview ? 'production' : 'qa'
const configuredApiBaseUrl = normalizeBaseUrl(trimmed(import.meta.env.VITE_API_BASE_URL))
const apiBaseUrl = resolveApiBaseUrl(configuredApiBaseUrl)
const configuredContentVersion = trimmed(import.meta.env.VITE_CONTENT_VERSION)
const contentVersion = configuredContentVersion || (approvedPagesPreview ? 'preview' : '')
const configuredProductionDataMode = trimmed(import.meta.env.VITE_PRODUCTION_DATA_MODE)
const productionDataMode = configuredProductionDataMode || (approvedPagesPreview ? 'api' : '')
const configuredSupabaseUrl = normalizeBaseUrl(trimmed(import.meta.env.VITE_SUPABASE_URL))
const configuredSupabasePublishableKey = trimmed(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY)
const supabaseUrl = resolveSupabaseUrl(configuredSupabaseUrl)
const supabasePublishableKey = resolveSupabasePublishableKey(configuredSupabasePublishableKey)

// Production uses the reviewed API-only customer and admin component tree.
// QA continues to use deterministic mocks and its explicit state/role toolbar.
const productionApiFrontendImplemented = true
const publicProductionIssues: string[] = []

if (mode === 'production') {
  if (!apiBaseUrl) publicProductionIssues.push('VITE_API_BASE_URL is required.')
  else if (!apiBaseUrl.startsWith('https://')) publicProductionIssues.push('VITE_API_BASE_URL must use HTTPS.')
  if (!contentVersion) publicProductionIssues.push('VITE_CONTENT_VERSION is required.')
  if (productionDataMode !== 'api') publicProductionIssues.push('VITE_PRODUCTION_DATA_MODE must be api.')
  if (!productionApiFrontendImplemented) publicProductionIssues.push('Production API-backed frontend integration is not complete.')
}

const adminProductionIssues = [...publicProductionIssues]
if (mode === 'production') {
  if (!supabaseUrl) adminProductionIssues.push('VITE_SUPABASE_URL is required for protected admin sign-in.')
  else if (!supabaseUrl.startsWith('https://')) adminProductionIssues.push('VITE_SUPABASE_URL must use HTTPS.')
  if (!supabasePublishableKey) adminProductionIssues.push('VITE_SUPABASE_PUBLISHABLE_KEY is required for protected admin sign-in.')
}

export const runtimeConfig = Object.freeze({
  mode,
  isProduction: mode === 'production',
  qaEnabled: mode === 'qa',
  apiBaseUrl,
  contentVersion,
  productionDataMode,
  supabaseUrl,
  supabasePublishableKey,
  productionApiFrontendImplemented,
  publicProductionIssues: Object.freeze(publicProductionIssues),
  adminProductionIssues: Object.freeze(adminProductionIssues),
  // Kept as the combined legacy contract for diagnostics and older verifiers.
  productionIssues: Object.freeze(adminProductionIssues),
})
